import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Building2, Calendar, CheckCircle, XCircle } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { organizationService } from '@/services/organization'
import { useOrganizationStore } from '@/stores/organization-store'
import type { Organization } from '@/schemas/organization'

// Loading skeleton
function OrganizationCardSkeleton() {
  return (
    <Card variant="glass" className="p-6 animate-pulse">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-gray-700/50 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-5 bg-gray-700/50 rounded w-1/2" />
          <div className="h-4 bg-gray-700/50 rounded w-1/3" />
          <div className="flex gap-2 mt-3">
            <div className="h-5 w-16 bg-gray-700/50 rounded-full" />
            <div className="h-5 w-20 bg-gray-700/50 rounded-full" />
          </div>
        </div>
      </div>
    </Card>
  )
}

// Organization card
interface OrganizationCardProps {
  organization: Organization
  onSelect: (org: Organization) => void
}

function OrganizationCard({ organization, onSelect }: OrganizationCardProps) {
  const { t } = useTranslation(['organizations', 'common'])

  const roleKey = organization.user_role?.toLowerCase() as
    | 'owner'
    | 'admin'
    | 'editor'
    | 'viewer'
    | undefined

  const formattedDate = new Date(organization.created_at).toLocaleDateString()

  return (
    <Card
      variant="glass"
      className="p-6 hover:bg-white/10 transition-colors cursor-pointer group"
      onClick={() => onSelect(organization)}
    >
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
          <Building2 className="w-7 h-7 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-100 truncate group-hover:text-white transition-colors">
            {organization.name}
          </h3>
          <p className="text-sm text-gray-400 font-mono truncate mt-0.5">
            @{organization.slug}
          </p>
          {organization.description && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
              {organization.description}
            </p>
          )}

          <div className="flex flex-wrap gap-2 mt-3">
            {/* Status badge */}
            {organization.is_active ? (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full">
                <CheckCircle className="w-3 h-3" />
                {t('common:status.active')}
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-medium rounded-full">
                <XCircle className="w-3 h-3" />
                {t('common:status.inactive')}
              </span>
            )}

            {/* Role badge */}
            {roleKey && (
              <span className="px-2 py-0.5 bg-indigo-500/20 text-indigo-300 text-xs font-medium rounded-full capitalize">
                {t(`roles.${roleKey}`)}
              </span>
            )}

            {/* Created date */}
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <Calendar className="w-3 h-3" />
              {t('list.createdAt')} {formattedDate}
            </span>
          </div>
        </div>
      </div>
    </Card>
  )
}

// Empty state
function EmptyState() {
  const { t } = useTranslation('organizations')

  return (
    <div className="col-span-full flex flex-col items-center justify-center py-16">
      <div className="w-20 h-20 rounded-2xl bg-gray-700/30 flex items-center justify-center mb-6">
        <Building2 className="w-10 h-10 text-gray-500" />
      </div>
      <h3 className="text-xl font-semibold text-gray-200 mb-2">
        {t('list.noOrganizations')}
      </h3>
      <p className="text-gray-500 text-center max-w-md">
        {t('list.noOrganizationsDesc')}
      </p>
    </div>
  )
}

export function OrganizationsPage() {
  const { t } = useTranslation('organizations')
  const navigate = useNavigate()
  const { setCurrentOrganization } = useOrganizationStore()

  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()

    async function fetchOrganizations() {
      setIsLoading(true)
      try {
        const response = await organizationService.getOrganizations({ signal: controller.signal })
        setOrganizations(response.organizations)
      } catch (error) {
        if ((error as Error).name !== 'CanceledError') {
          console.error('Failed to fetch organizations:', error)
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    fetchOrganizations()
    return () => controller.abort()
  }, [])

  const handleSelectOrg = (org: Organization) => {
    setCurrentOrganization(org)
    navigate('/dashboard')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-100">{t('title')}</h1>
        <p className="text-gray-400 mt-1">{t('subtitle')}</p>
      </div>

      {/* Organizations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          <>
            {Array.from({ length: 3 }).map((_, index) => (
              <OrganizationCardSkeleton key={index} />
            ))}
          </>
        ) : organizations.length === 0 ? (
          <EmptyState />
        ) : (
          organizations.map((org) => (
            <OrganizationCard
              key={org.id}
              organization={org}
              onSelect={handleSelectOrg}
            />
          ))
        )}
      </div>

    </div>
  )
}
